import { useEffect, useRef } from "react";
import * as THREE from "three";

const HQ = { lat: 19.24, lng: 73.13, name: "Kalyan HQ", hq: true };
const LOCATIONS = [
  HQ,
  { lat: 38.9, lng: -77.03, name: "United States" },
  { lat: 48.14, lng: 11.58, name: "European Union" },
  { lat: 51.5, lng: -0.12, name: "United Kingdom" },
  { lat: 35.68, lng: 139.69, name: "Japan" },
  { lat: 39.9, lng: 116.4, name: "China" },
  { lat: 37.56, lng: 126.97, name: "South Korea" },
  { lat: 1.35, lng: 103.82, name: "Singapore" },
  { lat: -33.86, lng: 151.2, name: "Australia" },
  { lat: 45.42, lng: -75.7, name: "Canada" },
  { lat: 25.2, lng: 55.27, name: "UAE" },
  { lat: -23.55, lng: -46.63, name: "Brazil" },
  { lat: -26.2, lng: 28.05, name: "South Africa" },
];

function latLngToVec3(lat, lng, r) {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lng + 180) * Math.PI) / 180;
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

export default function Globe3D() {
  const mountRef = useRef(null);
  const tipRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    const tip = tipRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.set(0, 0.35, 3.1);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const globe = new THREE.Group();
    globe.rotation.x = 0.18;
    globe.rotation.y = -1.35;
    scene.add(globe);

    const earthTex = new THREE.TextureLoader().load("/textures/earth.jpg");
    earthTex.colorSpace = THREE.SRGBColorSpace;
    globe.add(new THREE.Mesh(
      new THREE.SphereGeometry(1, 64, 64),
      new THREE.MeshPhongMaterial({ map: earthTex, shininess: 6 })
    ));

    globe.add(new THREE.Mesh(
      new THREE.SphereGeometry(1.003, 36, 24),
      new THREE.MeshBasicMaterial({ color: 0xD4AF37, wireframe: true, transparent: true, opacity: 0.045 })
    ));

    scene.add(new THREE.Mesh(
      new THREE.SphereGeometry(1.14, 64, 64),
      new THREE.MeshBasicMaterial({ color: 0xD4AF37, transparent: true, opacity: 0.05, side: THREE.BackSide })
    ));

    const starPos = new Float32Array(900 * 3);
    for (let i = 0; i < 900; i++) {
      const r = 18 + Math.random() * 30;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      starPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      starPos[i * 3 + 1] = r * Math.cos(phi);
      starPos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute("position", new THREE.BufferAttribute(starPos, 3));
    scene.add(new THREE.Points(starGeo, new THREE.PointsMaterial({ color: 0xA3B8CC, size: 0.045, transparent: true, opacity: 0.65 })));

    const pinMat = new THREE.MeshBasicMaterial({ color: 0xD4AF37 });
    const hqMat = new THREE.MeshBasicMaterial({ color: 0xE6C657 });
    const up = new THREE.Vector3(0, 1, 0);
    LOCATIONS.forEach((loc) => {
      const pos = latLngToVec3(loc.lat, loc.lng, 1);
      const pin = new THREE.Group();
      pin.userData.pinName = loc.name;
      const mat = loc.hq ? hqMat : pinMat;
      const s = loc.hq ? 1.5 : 1;
      const cone = new THREE.Mesh(new THREE.ConeGeometry(0.013 * s, 0.055 * s, 8), mat);
      cone.position.y = 0.05 * s;
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.021 * s, 12, 12), mat);
      head.position.y = 0.09 * s;
      pin.add(cone, head);
      pin.position.copy(pos);
      pin.quaternion.setFromUnitVectors(up, pos.clone().normalize());
      globe.add(pin);
    });

    const hqPos = latLngToVec3(HQ.lat, HQ.lng, 1);
    const arcMat = new THREE.MeshBasicMaterial({ color: 0xD4AF37, transparent: true, opacity: 0.45 });
    LOCATIONS.filter((l) => !l.hq).forEach((loc) => {
      const end = latLngToVec3(loc.lat, loc.lng, 1);
      const lift = 1 + hqPos.distanceTo(end) * 0.3;
      const mid = hqPos.clone().add(end).multiplyScalar(0.5).normalize().multiplyScalar(lift);
      const curve = new THREE.QuadraticBezierCurve3(hqPos, mid, end);
      globe.add(new THREE.Mesh(new THREE.TubeGeometry(curve, 48, 0.003, 6, false), arcMat));
    });

    scene.add(new THREE.AmbientLight(0xffffff, 1.15));
    const sun = new THREE.DirectionalLight(0xfff2cf, 1.5);
    sun.position.set(-3, 2, 4);
    scene.add(sun);

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hovered = null;
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    let spinVel = 0;

    renderer.domElement.style.cursor = "grab";
    renderer.domElement.style.touchAction = "none";

    const clearHover = () => {
      if (hovered) hovered.scale.setScalar(1);
      hovered = null;
      tip.classList.add("hidden");
      renderer.domElement.style.cursor = dragging ? "grabbing" : "grab";
    };

    const onDown = (e) => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      renderer.domElement.setPointerCapture(e.pointerId);
      renderer.domElement.style.cursor = "grabbing";
      tip.classList.add("hidden");
    };

    const onDragMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      globe.rotation.y += dx * 0.005;
      globe.rotation.x = Math.max(-0.35, Math.min(0.9, globe.rotation.x + dy * 0.003));
      spinVel = dx * 0.005;
    };

    const onUp = () => {
      dragging = false;
      renderer.domElement.style.cursor = hovered ? "pointer" : "grab";
    };

    const onMove = (e) => {
      if (dragging) return;
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(globe.children, true);
      let pin = null;
      for (const h of hits) {
        let o = h.object;
        while (o && !o.userData.pinName) o = o.parent;
        if (o && o !== globe) { pin = o; break; }
      }
      if (pin !== hovered) {
        if (hovered) hovered.scale.setScalar(1);
        hovered = pin;
        if (hovered) {
          hovered.scale.setScalar(1.7);
          tip.textContent = hovered.userData.pinName;
          tip.classList.remove("hidden");
          renderer.domElement.style.cursor = "pointer";
        } else {
          tip.classList.add("hidden");
          renderer.domElement.style.cursor = "grab";
        }
      }
      if (hovered) {
        tip.style.left = `${e.clientX - rect.left}px`;
        tip.style.top = `${e.clientY - rect.top}px`;
      }
    };

    renderer.domElement.addEventListener("pointerdown", onDown);
    renderer.domElement.addEventListener("pointermove", onMove);
    renderer.domElement.addEventListener("pointerleave", clearHover);
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onUp);

    let raf;
    const animate = () => {
      if (!dragging) {
        globe.rotation.y += 0.0011 + spinVel;
        spinVel *= 0.94;
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const ro = new ResizeObserver(() => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    });
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onDown);
      renderer.domElement.removeEventListener("pointermove", onMove);
      renderer.domElement.removeEventListener("pointerleave", clearHover);
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onUp);
      scene.traverse((o) => {
        o.geometry?.dispose?.();
        const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
        mats.forEach((m) => { m.map?.dispose?.(); m.dispose?.(); });
      });
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div className="relative w-full h-[420px] sm:h-[520px]">
      <div
        ref={mountRef}
        data-testid="globe-3d"
        className="absolute inset-0"
        aria-label="Slowly rotating earth with gold pins marking ClaimSpace filing locations worldwide"
      />
      <div
        ref={tipRef}
        data-testid="globe-tooltip"
        className="absolute hidden pointer-events-none z-10 -translate-x-1/2 -translate-y-[220%] bg-navy-900/90 backdrop-blur border border-gold/40 text-gold text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 whitespace-nowrap"
      />
      <p className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-mist/50 pointer-events-none">
        Drag to explore
      </p>
    </div>
  );
}
